import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { LinearProgress, Typography, useTheme } from '@mui/material';
import { CSSProperties } from 'styled-components';
import { UploadOptions } from '@mnz/builder';
import { px2rem } from '../../core';
import ImageIcon from '../icon/ImageIcon';
import CloseIcon from '../icon/CloseIcon';
import { isValidImage, IMAGE_FILE_TYPE } from '../../shared/utils/image';
import { convertToMb } from '../../shared/utils/file';
import { Plus } from '@menuzen/icon/build/outline';

interface UploaderProps extends React.HTMLAttributes<HTMLDivElement> {
    value?: string;
    onSuccess: (url: string) => void;
    onRemove?: () => void;
    uploadImage: (file: File, options?: Partial<UploadOptions>) => Promise<string>;
    imgCss?: CSSProperties;
    maxSize?: number;
    disabled?: boolean;
}

const Uploader: FC<UploaderProps> = ({
    value,
    uploadImage,
    onSuccess,
    maxSize = 10 * 1024 * 1024, // default 10MB
    onRemove,
    imgCss,
    disabled = false,
    ...props
}) => {
    const theme = useTheme();
    const [message, setMessage] = useState<null | string>(null);
    const [progress, setProgress] = useState<number | null>(null);

    const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        const { files } = e.target;
        const file = files?.item(0);
        if (!file) {
            setMessage('Please select image.');
            return;
        }
        if (!isValidImage(file)) {
            setMessage(`${IMAGE_FILE_TYPE.join(' ')} only`);
            return;
        }

        if (file.size > maxSize) {
            setMessage(`Image size must be less than ${convertToMb(maxSize)}.`);
            return;
        }
        setMessage('');
        setProgress(0);
        e.target.value = '';
        try {
            const res = await uploadImage(file, {
                onUploadProgress: setProgress,
            });
            onSuccess(res);
        } catch (error: unknown) {
            console.log('error', error);
            setMessage('Somethings went wrong.');
        }
        setProgress(null);
    };

    const handleRemove = () => {
        if (onRemove) {
            setProgress(null);
            onRemove();
        }
    };
    return (
        <>
            <div {...props}>
                <div
                    css={{
                        position: 'relative',
                        height: '100%',
                    }}
                >
                    <div
                        css={{
                            paddingBottom: `${((85 / 300) * 100).toFixed(4)}%`,
                        }}
                    />
                    <div
                        css={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            color: theme.palette.primary50,
                            objectFit: 'cover',
                            objectPosition: 'center',
                        }}
                    >
                        <input
                            css={{
                                opacity: 0,
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1,
                            }}
                            type="file"
                            accept={'image/*'}
                            onChange={handleUpload}
                            disabled={disabled}
                        />
                        {progress === null && (
                            <>
                                {!value ? (
                                    <div
                                        css={{
                                            flexGrow: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            border: `1px dashed ${theme.palette.primary40}`,
                                        }}
                                    >
                                        <div css={{ fontSize: 20 }}>
                                            <Plus />
                                        </div>
                                        <p css={{ fontWeight: 600, marginLeft: px2rem(10) }}>Upload Image</p>
                                    </div>
                                ) : (
                                    <Fragment>
                                        <img
                                            src={value}
                                            css={{
                                                objectFit: 'contain',
                                                height: '100%',
                                                ...imgCss,
                                            }}
                                        />
                                        <div
                                            css={{
                                                position: 'absolute',
                                                right: px2rem(4),
                                                top: px2rem(4),
                                                zIndex: 10,
                                                background: '#FFFFFF',
                                                boxShadow: '0.747621px 2.24286px 5.23335px rgba(0, 0, 0, 0.15)',
                                                borderRadius: 4,
                                                width: px2rem(30),
                                                height: px2rem(30),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: theme.palette.primary.main,
                                                cursor: 'pointer',
                                            }}
                                            onClick={handleRemove}
                                        >
                                            <CloseIcon />
                                        </div>
                                    </Fragment>
                                )}
                            </>
                        )}

                        {progress !== null && (
                            <div
                                css={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    cursor: 'pointer',
                                }}
                            >
                                <div
                                    css={{
                                        width: '100%',
                                    }}
                                >
                                    <LinearProgress
                                        variant="determinate"
                                        color={'primary'}
                                        value={progress}
                                        sx={{ height: px2rem(6), borderRadius: 4 }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {message && (
                <Typography sx={{ my: 1 }} color="error">
                    {message}
                </Typography>
            )}
        </>
    );
};

export default Uploader;
